/**
 * Vanilla WebSocket client for Taskydo chat.
 *
 * No Socket.IO, no extra frameworks — the browser WebSocket API only.
 * React (and anything else) imports this module and feeds events into
 * the existing message renderer.
 */

import { apiOrigin } from '../config';

export const CLOSE_NO_RECONNECT = new Set([4001, 4003]);

const BACKOFF_START_MS = 1000;
const BACKOFF_CAP_MS = 30000;
const PING_INTERVAL_MS = 25000;
const SEND_TIMEOUT_MS = 20000;

/**
 * Build ``ws://`` / ``wss://`` URL for a conversation.
 * Token is attached as ``?token=`` so Daphne/Channels can authenticate
 * without a cookie (JWT is the SPA's primary scheme).
 */
export function chatSocketUrl(roomId, token) {
  const origin = apiOrigin() || window.location.origin;
  const page = new URL(origin);
  const proto = page.protocol === 'https:' ? 'wss' : 'ws';
  const path = `/ws/chat/${roomId}/`;
  const url = `${proto}://${page.host}${path}`;
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

export function newClientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Infer ticks for a message the current user sent, using other
 * participants' ``last_read_at`` (already on the conversation payload).
 */
export function inferMessageStatus(message, conversation, currentUserId) {
  if (!message) return null;
  if (message.delivery_status === 'error') return 'error';
  if (message.delivery_status === 'pending') return 'pending';
  if (currentUserId == null || message.sender !== currentUserId) return null;
  const others = ((conversation && conversation.participants) || []).filter(
    (p) => (p.user_detail ? p.user_detail.id : p.user) !== currentUserId,
  );
  const created = message.created_on ? new Date(message.created_on).getTime() : 0;
  if (others.length && created) {
    const allRead = others.every((p) => {
      if (!p.last_read_at) return false;
      return new Date(p.last_read_at).getTime() >= created;
    });
    if (allRead) return 'read';
  }
  if (message.delivery_status === 'delivered') return 'delivered';
  if (message.delivery_status === 'read') return 'read';
  return 'sent';
}

export function statusLabel(status, error) {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'sent':
      return 'Sent';
    case 'delivered':
      return 'Delivered';
    case 'read':
      return 'Read';
    case 'error':
      return error || 'Server error';
    default:
      return '';
  }
}

/**
 * @param {object} options
 * @param {number|string} options.roomId
 * @param {() => string|null} [options.getToken]
 * @param {(event: object) => void} options.onEvent
 * @param {(state: string, meta?: object) => void} [options.onConnectionChange]
 * @param {() => void} [options.onReconnected]
 */
export function createChatSocket(options) {
  const {
    roomId,
    getToken = () => null,
    onEvent,
    onConnectionChange = () => {},
    onReconnected = () => {},
  } = options;

  let socket = null;
  let closedByUser = false;
  let attempt = 0;
  let reconnectTimer = null;
  let pingTimer = null;
  let openedOnce = false;
  let state = 'connecting';

  const pendingAcks = new Map();
  const outboundQueue = [];

  function setState(next, meta) {
    state = next;
    onConnectionChange(next, meta || {});
  }

  function clearPing() {
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  }

  function startPing() {
    clearPing();
    pingTimer = setInterval(() => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, PING_INTERVAL_MS);
  }

  function flushQueue() {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    while (outboundQueue.length) {
      socket.send(outboundQueue.shift());
    }
  }

  function connect() {
    if (closedByUser) return;
    setState(openedOnce ? 'reconnecting' : 'connecting');
    const token = typeof getToken === 'function' ? getToken() : getToken;
    const url = chatSocketUrl(roomId, token);
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      scheduleReconnect();
      return;
    }
    socket = ws;

    ws.onopen = () => {
      attempt = 0;
      startPing();
      flushQueue();
      setState('open');
      if (openedOnce) onReconnected();
      openedOnce = true;
    };

    ws.onmessage = (evt) => {
      let parsed;
      try {
        parsed = JSON.parse(evt.data);
      } catch {
        return;
      }
      if (parsed && parsed.type === 'pong') return;
      if (parsed && parsed.client_id && pendingAcks.has(parsed.client_id)) {
        const entry = pendingAcks.get(parsed.client_id);
        if (entry && entry.timer) clearTimeout(entry.timer);
        pendingAcks.delete(parsed.client_id);
      }
      onEvent(parsed);
    };

    ws.onerror = () => {
      // onclose follows; surface nothing extra so the reconnect banner
      // is the single status line.
    };

    ws.onclose = (evt) => {
      clearPing();
      socket = null;
      const code = evt.code;
      if (closedByUser) {
        setState('closed', { code });
        return;
      }
      if (CLOSE_NO_RECONNECT.has(code)) {
        const reason =
          code === 4001
            ? 'You are not signed in. Refresh the page to log in again.'
            : 'You do not have access to this conversation.';
        setState('auth_failed', { code, reason });
        failQueued(reason);
        return;
      }
      scheduleReconnect(code);
    };
  }

  function scheduleReconnect(code) {
    if (closedByUser) return;
    const delay = Math.min(
      BACKOFF_START_MS * 2 ** attempt,
      BACKOFF_CAP_MS,
    );
    attempt += 1;
    setState('reconnecting', { code, delay });
    reconnectTimer = setTimeout(connect, delay);
  }

  function failQueued(reason) {
    outboundQueue.length = 0;
    pendingAcks.forEach((entry) => {
      if (entry.timer) clearTimeout(entry.timer);
      if (entry.clientId) {
        onEvent({
          type: 'error',
          status: 'error',
          error: reason,
          client_id: entry.clientId,
        });
      }
    });
    pendingAcks.clear();
  }

  function sendMessage(text, clientId) {
    const frame = JSON.stringify({
      message: text,
      client_id: clientId,
    });
    if (state === 'auth_failed') {
      onEvent({
        type: 'error',
        status: 'error',
        error: 'Cannot send: not authorised.',
        client_id: clientId,
      });
      return false;
    }
    const entry = { clientId, timer: null };
    entry.timer = setTimeout(() => {
      if (!pendingAcks.has(clientId)) return;
      pendingAcks.delete(clientId);
      onEvent({
        type: 'error',
        status: 'error',
        error: 'Server error: no acknowledgement. Please retry.',
        client_id: clientId,
      });
    }, SEND_TIMEOUT_MS);
    pendingAcks.set(clientId, entry);

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(frame);
      return true;
    }
    outboundQueue.push(frame);
    return false;
  }

  function sendJson(obj) {
    const frame = JSON.stringify(obj);
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(frame);
      return true;
    }
    return false;
  }

  function close() {
    closedByUser = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    clearPing();
    pendingAcks.forEach((entry) => {
      if (entry.timer) clearTimeout(entry.timer);
    });
    pendingAcks.clear();
    outboundQueue.length = 0;
    if (socket) {
      try {
        socket.close(1000, 'client leaving');
      } catch {
        /* already closed */
      }
      socket = null;
    }
    setState('closed');
  }

  connect();

  return {
    sendMessage,
    sendJson,
    close,
    getState: () => state,
  };
}
